import * as React from 'react'
import clsx from 'clsx'
import * as plume from '@ulule/owl-kit-components/next'

import { Container } from '../../_shared/Container'
import { VALUE_CARDS, type ValueCardIcon, type ValueCardVariant } from '../content'
import { OwlSlideshow } from '../components/OwlSlideshow'
import { Mission } from './Mission'
import styles from './Makers.module.css'

const VARIANT_CLASS: Record<ValueCardVariant, string> = {
  yellow: styles.valueYellow,
  blue: styles.valueBlue,
  pink: styles.valuePink,
  mint: styles.valueMint,
}

function ValueIcon({ icon }: { icon: ValueCardIcon }): React.ReactElement {
  switch (icon) {
    case 'bulb':
      return <plume.glyphs.stroke.Bulb size={24} />
    case 'people':
      return <plume.glyphs.stroke.People size={24} />
    case 'megaphone':
      return <plume.glyphs.stroke.Megaphone size={24} />
    case 'star':
      return <plume.glyphs.stroke.Star size={24} />
  }
}

// Filled star for the (static) Trustpilot rating row below — see the FIXME there.
function StarIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  )
}

export function Makers(): React.ReactElement {
  return (
    <section className={styles.makers} id="makers">
      <Container className={styles.makers__stack}>
        <div className={styles.makers__head}>
          <div className={styles.makers__intro}>
            <h2 className={styles.makers__title}>
              Au service des <em>makers</em> 💪
            </h2>
            <p className={styles.makers__lede}>
              Ulule accompagne des milliers de projets créatifs, solidaires et innovants. Derrière chaque
              collecte réussie, il y a une équipe qui y croit : techno &amp; produit, expertise, conseils,
              partenariats, nous voulons proposer la meilleure plateforme pour lancer ou développer vos
              projets&nbsp;!
            </p>
          </div>
          <div className={styles.makers__portrait}>
            <OwlSlideshow />
          </div>
        </div>

        <div className={styles.values}>
          {VALUE_CARDS.map((card) => (
            <article
              key={card.title}
              className={clsx(styles.value, VARIANT_CLASS[card.variant])}
              style={{ '--rot': `${card.rotate}deg` } as React.CSSProperties}
            >
              <span className={styles.value__icon}>
                <ValueIcon icon={card.icon} />
              </span>
              <h3 className={styles.value__title}>{card.title}</h3>
              <p className={styles.value__body}>{card.body}</p>
            </article>
          ))}
        </div>

        {/* FIXME(trustpilot): this rating row is static. Make it dynamic by rendering the live Duvet
            `<Trustpilot>` widget instead — it can't be SSR'd here (it imports `@owl-nest/models`, which
            reads UFE config at module-eval time, absent in the standalone prerender), so it must be
            mounted client-only / lazily. The real widget already runs in the client-mounted footer. */}
        <div className={styles.trust}>
          <b className={styles.trust__label}>Excellent</b>
          <span className={styles.trust__stars} aria-hidden="true">
            {[0, 1, 2, 3, 4].map((index) => (
              <span key={index} className={styles.trust__star}>
                <StarIcon />
              </span>
            ))}
          </span>
          <a
            className={styles.trust__link}
            href="https://fr.trustpilot.com/review/ulule.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className={styles.trust__logoStar}>
              <StarIcon />
            </span>
            Trustpilot
          </a>
        </div>

        <Mission />
      </Container>
    </section>
  )
}
